import { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Loader2, CheckCircle2, Phone, Mail, MapPin, Facebook, Linkedin } from "lucide-react";
import { toast } from "sonner";
import { PRODUCT_OPTIONS, PROPERTY_OPTIONS, CONTACT, SOCIALS } from "./data";
import { Reveal } from "./motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PinterestIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2C6.48 2 2 6.48 2 12c0 4.24 2.64 7.86 6.36 9.31-.09-.79-.17-2 .04-2.87.18-.78 1.18-4.99 1.18-4.99s-.3-.6-.3-1.49c0-1.4.81-2.44 1.82-2.44.86 0 1.27.64 1.27 1.41 0 .86-.55 2.15-.83 3.34-.24 1 .5 1.81 1.48 1.81 1.78 0 3.15-1.88 3.15-4.59 0-2.4-1.72-4.08-4.19-4.08-2.85 0-4.53 2.14-4.53 4.35 0 .86.33 1.79.75 2.29.08.1.09.19.07.29-.08.31-.25 1-.28 1.14-.04.19-.15.23-.34.14-1.25-.58-2.03-2.4-2.03-3.87 0-3.15 2.29-6.04 6.6-6.04 3.47 0 6.16 2.47 6.16 5.77 0 3.45-2.17 6.22-5.19 6.22-1.01 0-1.97-.53-2.29-1.15l-.62 2.38c-.23.87-.84 1.96-1.25 2.63.94.29 1.94.45 2.97.45 5.52 0 10-4.48 10-10S17.52 2 12 2z" />
  </svg>
);

const SOCIAL_ICONS = { Facebook, LinkedIn: Linkedin, Pinterest: PinterestIcon };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs tracking-[0.15em] uppercase text-zinc-500 mb-3 font-display">{label}</span>
    {children}
  </label>
);

const inputCls =
  "w-full bg-transparent border-b border-[#333] focus:border-[#fcc41b] text-white text-lg py-3 outline-none transition-colors duration-300 placeholder:text-zinc-600";

export const QuoteFooter = ({ productOptions = PRODUCT_OPTIONS, blurb = "Tell us about your project and our team will recommend the right SolarArk solution, confirm sizing, and connect you with our national installer network — or support your DIY install. No obligation." }) => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", postcode: "", property_type: "", product_interest: "", message: "", company_website: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, form);
      setDone(true);
      toast.success("Thanks — our team will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", postcode: "", property_type: "", product_interest: "", message: "", company_website: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.[0]?.msg || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" data-testid="footer-section" className="relative bg-[#050505] border-t border-[#1c1c1c] pt-28 lg:pt-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-16 lg:gap-24">
          {/* Left copy */}
          <div>
            <Reveal>
              <p className="text-xs tracking-[0.3em] uppercase text-[#fcc41b] font-display font-medium mb-6">
                Request a Quote
              </p>
              <h2 className="font-display font-semibold tracking-tighter leading-[0.92] text-white"
                style={{ fontSize: "clamp(2.5rem, 6vw, 5.5rem)" }}>
                Let's talk<br />solar.
              </h2>
              <p className="mt-8 text-zinc-400 text-lg leading-relaxed max-w-md">
                {blurb}
              </p>

              <div className="mt-12 space-y-5">
                <a href={CONTACT.phoneHref} data-testid="contact-phone" className="flex items-center gap-4 text-zinc-300 hover:text-[#fcc41b] transition-colors duration-300">
                  <span className="h-11 w-11 rounded-full border border-[#333] flex items-center justify-center"><Phone size={18} /></span>
                  {CONTACT.phone}
                </a>
                <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="flex items-center gap-4 text-zinc-300 hover:text-[#fcc41b] transition-colors duration-300">
                  <span className="h-11 w-11 rounded-full border border-[#333] flex items-center justify-center"><Mail size={18} /></span>
                  {CONTACT.email}
                </a>
                <div className="flex items-center gap-4 text-zinc-300">
                  <span className="h-11 w-11 rounded-full border border-[#333] flex items-center justify-center"><MapPin size={18} /></span>
                  Servicing all of Australia
                </div>
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <Reveal delay={0.1}>
            {done ? (
              <div data-testid="quote-success" className="rounded-3xl border border-[#fcc41b]/30 bg-[#0d0d0d] p-12 flex flex-col items-start gap-6 h-full justify-center">
                <CheckCircle2 size={56} className="text-[#fcc41b]" />
                <h3 className="font-display font-semibold text-3xl text-white">Request received.</h3>
                <p className="text-zinc-400 text-lg leading-relaxed">
                  Thank you for reaching out to SolarArk. One of our solar specialists will contact you within one business day.
                </p>
                <button onClick={() => setDone(false)} className="text-[#fcc41b] font-display font-medium" data-testid="quote-reset">
                  Submit another request
                </button>
              </div>
            ) : (
              <form onSubmit={submit} noValidate data-testid="quote-form" className="rounded-3xl border border-[#262626] bg-[#0d0d0d] p-8 lg:p-12 grid sm:grid-cols-2 gap-x-8 gap-y-8">
                <input
                  type="text"
                  name="company_website"
                  value={form.company_website}
                  onChange={set("company_website")}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute -left-[9999px] top-auto h-px w-px opacity-0 pointer-events-none"
                />
                <Field label="Full Name *">
                  <input data-testid="quote-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane Smith" required />
                </Field>
                <Field label="Email *">
                  <input data-testid="quote-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="jane@email.com" required />
                </Field>
                <Field label="Phone">
                  <input data-testid="quote-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="0400 000 000" />
                </Field>
                <Field label="Postcode">
                  <input data-testid="quote-postcode" className={inputCls} value={form.postcode} onChange={set("postcode")} placeholder="2000" />
                </Field>
                <Field label="Property Type">
                  <select data-testid="quote-property" className={`${inputCls} cursor-pointer`} value={form.property_type} onChange={set("property_type")}>
                    <option value="" className="bg-[#0d0d0d]">Select…</option>
                    {PROPERTY_OPTIONS.map((o) => <option key={o} value={o} className="bg-[#0d0d0d]">{o}</option>)}
                  </select>
                </Field>
                <Field label="Product Interest">
                  <select data-testid="quote-product" className={`${inputCls} cursor-pointer`} value={form.product_interest} onChange={set("product_interest")}>
                    <option value="" className="bg-[#0d0d0d]">Select…</option>
                    {productOptions.map((o) => <option key={o} value={o} className="bg-[#0d0d0d]">{o}</option>)}
                  </select>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Message">
                    <textarea data-testid="quote-message" rows={3} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder="Tell us about your project…" />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    data-testid="quote-submit"
                    disabled={loading}
                    className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 rounded-full bg-[#fcc41b] text-black font-display font-semibold px-8 py-4 hover:bg-[#e5ae13] active:scale-95 transition-[background-color,transform] duration-300 disabled:opacity-60"
                  >
                    {loading ? <><Loader2 size={20} className="animate-spin" /> Sending…</> : <>Send Request <ArrowUpRight size={20} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" /></>}
                  </button>
                </div>
              </form>
            )}
          </Reveal>
        </div>

        {/* Footer bar */}
        <footer className="mt-28 border-t border-[#1c1c1c] py-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <img src="/solarark-logo-2026.png" alt="SolarArk Australia" className="h-10 w-auto object-contain" />
          <p className="text-zinc-500 text-sm text-center">
            © {new Date().getFullYear()} SolarArk Australia Pty Limited. Solar hot water & roof ventilation since 2009.
          </p>
          <div className="flex items-center gap-3" data-testid="social-links">
            {SOCIALS.map((s) => {
              const Icon = SOCIAL_ICONS[s.label] || Linkedin;
              return (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`SolarArk on ${s.label}`}
                  data-testid={`social-link-${s.label.toLowerCase()}`}
                  className="h-11 w-11 rounded-full border border-[#333] flex items-center justify-center text-zinc-400 hover:text-[#fcc41b] hover:border-[#fcc41b]/50 transition-colors duration-300"
                >
                  <Icon size={18} />
                </a>
              );
            })}
          </div>
        </footer>
      </div>
    </section>
  );
};
